import { apiRequest } from './api';

export interface Message {
    id: string;
    created_at: string;
    name: string | null;
    email?: string | null;
    message: string;
    parent_id?: string | null;
    likes?: number;
}

export interface ThreadedMessage extends Message {
    replies?: ThreadedMessage[];
}

interface SendMessageParams extends Omit<Message, 'id' | 'created_at' | 'likes'> {
    captchaToken: string;
}

export async function sendMessage({ name, email, message, captchaToken, parent_id }: SendMessageParams) {
    return apiRequest<{ id: string }>('/messages', {
        method: 'POST',
        body: JSON.stringify({ name: name?.trim() || null, email: email?.trim() || null, message, captchaToken, parent_id }),
    });
}

function threadMessages(messages: Message[]): ThreadedMessage[] {
    const messageMap = new Map<string, ThreadedMessage>();
    const rootMessages: ThreadedMessage[] = [];
    
    // First pass: create a map of all messages
    messages.forEach(msg => {
        const threadedMsg: ThreadedMessage = { ...msg, replies: [] };
        messageMap.set(msg.id, threadedMsg);
    });
    
    // Second pass: organize into threads
    messages.forEach(msg => {
        const threadedMsg = messageMap.get(msg.id)!;
        if (msg.parent_id && messageMap.has(msg.parent_id)) {
            const parent = messageMap.get(msg.parent_id)!;
            if (!parent.replies) parent.replies = [];
            parent.replies.push(threadedMsg);
        } else {
            rootMessages.push(threadedMsg);
        }
    });
    
    // Sort messages: root messages by newest first, replies by oldest first
    rootMessages.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    rootMessages.forEach(msg => {
        if (msg.replies) {
            msg.replies.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
        }
    });
    
    return rootMessages;
}

export async function fetchMessages(): Promise<ThreadedMessage[]> {
    return threadMessages(await apiRequest<Message[]>('/messages'));
}

export async function getMessageLikeCount(messageId: string): Promise<number> {
    return (await apiRequest<{ likes: number }>(`/messages/${encodeURIComponent(messageId)}/likes`)).likes;
}

export async function toggleMessageLike(messageId: string, isLiked: boolean): Promise<number> {
    return (await apiRequest<{ likes: number }>(`/messages/${encodeURIComponent(messageId)}/like`, {
        method: 'PUT', body: JSON.stringify({ liked: isLiked }),
    })).likes;
}
